// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: swipe a message to the right on the phone to reply to it, like
// WhatsApp and iMessage. The row follows the finger (eased past the trigger
// so it feels held), a reply arrow fades in behind it, the phone ticks once
// the swipe is far enough, and letting go there starts the reply. The drag
// writes CSS variables straight to the row once per frame, so the message
// never re-renders while a finger is down; React only hears about the start
// and the end (to mount and drop the arrow).

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {SWIPE_BACK_EDGE} from '@app/features/app/utils/SwipeBack';
import {swipeOffset} from '@app/features/konvo_whatsapp/utils/WhatsAppPresentation';
import {hapticTap} from '@app/features/platform/utils/Haptics';
import type React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';

/** Pixels of rightward travel that turn a swipe into a reply. */
export const SWIPE_REPLY_TRIGGER = 56;
/** Travel before the gesture picks an axis. */
const AXIS_SLOP = 10;
const SETTLE_MS = 200;
/** Things that own their own horizontal drag, or where a drag means something else. */
const IGNORE_SELECTOR =
	'input, textarea, select, [contenteditable="true"], video, audio, pre, [role="slider"], [data-swipe-reply-ignore]';

interface Gesture {
	x: number;
	y: number;
	mode: 'undecided' | 'swipe';
	offset: number;
	armed: boolean;
}

interface UseMessageSwipeReplyOptions {
	elementRef: React.RefObject<HTMLElement | null>;
	enabled: boolean;
	/** Checked when a finger lands, so permissions are only read on a touch. */
	canReply: () => boolean;
	onReply: () => void;
}

export interface MessageSwipeReply {
	/** True from the moment the swipe claims the gesture until the row has settled back. */
	active: boolean;
	onTouchStart: (event: React.TouchEvent) => void;
	onTouchMove: (event: React.TouchEvent) => void;
	/** Returns true when the touch was a swipe, so the caller can swallow the click that may follow. */
	onTouchEnd: () => boolean;
	onTouchCancel: () => void;
}

function scrollsSideways(element: Element, root: Element): boolean {
	for (let node: Element | null = element; node && node !== root; node = node.parentElement) {
		if (node.scrollWidth <= node.clientWidth) continue;
		const overflowX = getComputedStyle(node).overflowX;
		if (overflowX === 'auto' || overflowX === 'scroll') return true;
	}
	return false;
}

export function useMessageSwipeReply({
	elementRef,
	enabled,
	canReply,
	onReply,
}: UseMessageSwipeReplyOptions): MessageSwipeReply {
	const [active, setActive] = useState(false);
	const gestureRef = useRef<Gesture | null>(null);
	const frameRef = useRef<number | null>(null);
	const settleTimerRef = useRef<number | null>(null);

	const paint = useCallback(
		(offset: number) => {
			const element = elementRef.current;
			if (!element) return;
			element.style.setProperty('--swipe-reply-x', `${offset}px`);
			element.style.setProperty('--swipe-reply-progress', Math.min(1, offset / SWIPE_REPLY_TRIGGER).toFixed(3));
		},
		[elementRef],
	);

	const clear = useCallback(() => {
		const element = elementRef.current;
		if (element) {
			delete element.dataset.swipeReply;
			element.style.removeProperty('--swipe-reply-x');
			element.style.removeProperty('--swipe-reply-progress');
		}
		setActive(false);
	}, [elementRef]);

	const cancelFrame = useCallback(() => {
		if (frameRef.current != null) {
			cancelAnimationFrame(frameRef.current);
			frameRef.current = null;
		}
	}, []);

	const settle = useCallback(() => {
		cancelFrame();
		const element = elementRef.current;
		if (!element) {
			clear();
			return;
		}
		element.dataset.swipeReply = 'settling';
		paint(0);
		if (settleTimerRef.current != null) window.clearTimeout(settleTimerRef.current);
		settleTimerRef.current = window.setTimeout(
			() => {
				settleTimerRef.current = null;
				clear();
			},
			Accessibility.useReducedMotion ? 0 : SETTLE_MS,
		);
	}, [cancelFrame, clear, elementRef, paint]);

	useEffect(
		() => () => {
			cancelFrame();
			if (settleTimerRef.current != null) window.clearTimeout(settleTimerRef.current);
		},
		[cancelFrame],
	);

	const onTouchStart = useCallback(
		(event: React.TouchEvent) => {
			gestureRef.current = null;
			if (!enabled || event.touches.length !== 1) return;
			const touch = event.touches[0];
			const element = elementRef.current;
			const target = event.target instanceof Element ? event.target : null;
			if (!touch || !element || !target || touch.clientX < SWIPE_BACK_EDGE) return;
			if (target.closest(IGNORE_SELECTOR) || scrollsSideways(target, element)) return;
			if (!canReply()) return;
			gestureRef.current = {x: touch.clientX, y: touch.clientY, mode: 'undecided', offset: 0, armed: false};
		},
		[canReply, elementRef, enabled],
	);

	const onTouchMove = useCallback(
		(event: React.TouchEvent) => {
			const gesture = gestureRef.current;
			if (!gesture) return;
			const touch = event.touches[0];
			if (!touch || event.touches.length !== 1) {
				gestureRef.current = null;
				if (gesture.mode === 'swipe') settle();
				return;
			}
			const dx = touch.clientX - gesture.x;
			const dy = touch.clientY - gesture.y;
			if (gesture.mode === 'undecided') {
				if (Math.abs(dx) < AXIS_SLOP && Math.abs(dy) < AXIS_SLOP) return;
				// Only a rightward, mostly-horizontal start is ours; anything else is a scroll.
				if (dx <= 0 || Math.abs(dy) >= Math.abs(dx)) {
					gestureRef.current = null;
					return;
				}
				gesture.mode = 'swipe';
				if (settleTimerRef.current != null) {
					window.clearTimeout(settleTimerRef.current);
					settleTimerRef.current = null;
				}
				const element = elementRef.current;
				if (element) element.dataset.swipeReply = 'dragging';
				setActive(true);
			}
			gesture.offset = Math.max(0, swipeOffset(touch.clientX - gesture.x, SWIPE_REPLY_TRIGGER));
			const armed = gesture.offset >= SWIPE_REPLY_TRIGGER;
			if (armed && !gesture.armed) hapticTap(8);
			gesture.armed = armed;
			if (frameRef.current == null) {
				frameRef.current = requestAnimationFrame(() => {
					frameRef.current = null;
					const current = gestureRef.current;
					if (current?.mode === 'swipe') paint(current.offset);
				});
			}
		},
		[elementRef, paint, settle],
	);

	const onTouchEnd = useCallback(() => {
		const gesture = gestureRef.current;
		gestureRef.current = null;
		if (!gesture || gesture.mode !== 'swipe') return false;
		settle();
		// Synchronously, inside the touch, so iOS lets the composer take focus and raise the keyboard.
		if (gesture.armed) onReply();
		return true;
	}, [onReply, settle]);

	const onTouchCancel = useCallback(() => {
		const gesture = gestureRef.current;
		gestureRef.current = null;
		if (gesture?.mode === 'swipe') settle();
	}, [settle]);

	return {active, onTouchStart, onTouchMove, onTouchEnd, onTouchCancel};
}
